/**
 * CoachsPlaybook — public catalog page listing every downloadable Coach's Playbook.
 * Route: /coachs-playbook
 *
 * Each row is a lesson (Grade 8 currently) with a "Download Playbook" button
 * that fetches the branded PDF directly from the backend.
 */
import React, { useEffect, useState } from 'react';
import { Book, Download, Loader2, Lock } from 'lucide-react';
import api from '../services/api';

const SUBJECT_META = {
  math:            { label: 'Math',            color: '#F97316' },
  ela:             { label: 'ELA',             color: '#0EA5E9' },
  science:         { label: 'Science',         color: '#22C55E' },
  social_studies:  { label: 'Social Studies',  color: '#A78BFA' },
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function CoachsPlaybook() {
  const [rows, setRows] = useState([]);
  const [released, setReleased] = useState(false);
  const [releaseDate, setReleaseDate] = useState('');
  const [loading, setLoading] = useState(true);
  const searchParams = new URLSearchParams(window.location.search);
  const previewFlag = searchParams.get('preview') === '1';

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/academy/coachs-playbook/catalog${previewFlag ? '?preview=1' : ''}`);
        setRows(r.data.playbooks || []);
        setReleased(Boolean(r.data.released));
        setReleaseDate(r.data.release_date || '');
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [previewFlag]);

  const bySubject = rows.reduce((acc, r) => {
    const k = r.subject || 'other';
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

  const prettyDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4" data-testid="coachs-playbook-page">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-10">
          <div className="text-[10px] font-black tracking-[0.35em] text-amber-500 inline-flex items-center gap-2">
            <Book className="w-3.5 h-3.5" /> Coach's Playbook
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black leading-tight">The Long-Form Textbook.</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Every lesson expanded into a printable, deeper-reading playbook — chapter opener, deep dive, sports-logic case study, vocabulary drill, guided practice, answer key, and scholar notes pages.
            Print at home or take it into practice.
          </p>
        </div>

        {loading && (
          <div className="text-sm text-zinc-500 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading catalog…
          </div>
        )}

        {!loading && !released && (
          <div className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-10 text-center" data-testid="playbook-locked-splash">
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-black tracking-[0.3em] uppercase">
              <Lock className="w-4 h-4" /> Locked · Coming First Day of School
            </div>
            <h2 className="mt-4 text-3xl font-black">The Coach's Playbook drops on Day 1.</h2>
            <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
              We're saving the long-form textbooks for the first day of the new school year — <strong className="text-amber-300">{prettyDate(releaseDate)}</strong>.
              Come back on opening day to download the full library.
            </p>
            <div className="mt-6 text-[11px] text-zinc-500 italic">
              Admins can preview by adding <code className="text-amber-300">?preview=1</code> to the URL.
            </div>
          </div>
        )}

        {!loading && released && rows.length === 0 && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-8 text-zinc-400 text-sm">
            No playbooks published yet.
          </div>
        )}

        {!loading && released && Object.entries(bySubject).map(([subject, items]) => {
          const meta = SUBJECT_META[subject] || { label: subject, color: '#71717A' };
          return (
            <section key={subject} className="mb-10" data-testid={`playbook-subject-${subject}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block w-2 h-6" style={{ backgroundColor: meta.color }} />
                <h2 className="text-lg font-black uppercase tracking-widest text-white">{meta.label}</h2>
                <span className="text-xs text-zinc-500">{items.length} {items.length === 1 ? 'playbook' : 'playbooks'}</span>
              </div>
              <div className="border border-zinc-800">
                {items.map((r, i) => (
                  <div key={r.standard_code}
                       className={`flex flex-col sm:flex-row sm:items-center gap-3 p-5 ${i > 0 ? 'border-t border-zinc-900' : ''}`}>
                    <div className="sm:w-24 flex-shrink-0">
                      <div className="inline-block px-2 py-1 border font-mono text-[10px] font-bold"
                           style={{ borderColor: meta.color, color: meta.color }}>
                        {r.standard_code}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold">{r.title}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Grade {r.grade} · {meta.label}</div>
                    </div>
                    <a
                      href={`${API_BASE}${r.url}`}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-black tracking-widest uppercase text-black shrink-0"
                      style={{ backgroundColor: meta.color }}
                      data-testid={`playbook-download-${r.standard_code}`}
                    >
                      <Download className="w-4 h-4" /> Download Playbook
                    </a>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <p className="mt-12 text-[11px] text-zinc-500 leading-relaxed">
          Each playbook is generated fresh each time you download it — cover, deep dive, case study, vocabulary, guided practice, answer key, and blank scholar-notes pages. Print single-sided so students can write in the margins.
        </p>
      </div>
    </div>
  );
}
